import produce from 'immer';
import stringify from 'json-stable-stringify';
import isEqual from 'react-fast-compare';

import {EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  ResolutionContext,
  ResolvedValueInfo,
  resolveFromResolutionQueryPromise,
  ResolveQueryFn,
  resolveTypedValue,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {Note} from 'notebook/util/NoteUtilConstants';
import EntityStore from 'stores/EntityStore';

import {
  CustomResolutionQuery,
  isLookupColumnQuery,
  LookupColumnQuery,
  StringResolutionQuery,
  StringResolvedValue,
} from '../string-resolution/StringResolutionConstants';
import {filterLookupColumnQueries, NoteRuntimeSnapshot, QueryResolution} from '../TableStoreTransforms';
import {TableOpDiffMap} from './ops/TableOpConstants';

const RESOLUTION_TIMEOUT_SECONDS = 30;

export type ResolutionQuery = StringResolutionQuery | CustomResolutionQuery;

type QueryResolutionKey = string;

export interface QueryResolutionMapping {
  [queryResolutionKey: string]: QueryResolution;
}

export interface CellToQueryResolutionsMapping {
  [cellId: string]: Array<QueryResolution>;
}

// Exported for testing
export function _generateQueryResolutionKey(
  query: ResolutionQuery,
  tableProvenanceUuid: string
): QueryResolutionKey {
  return `${tableProvenanceUuid}_${stringify(query)}`;
}

// Exported for testing
export function _constructQueryResolutionMap(resolutions: Array<QueryResolution>): QueryResolutionMapping {
  const mapping: QueryResolutionMapping = {};
  for (const resolution of resolutions) {
    mapping[_generateQueryResolutionKey(resolution.query, resolution.provenanceUuid)] = resolution;
  }
  return mapping;
}

/**
 * For LookupColumnQueries, we released a change that added the lookupSortType field to the query, regardless
 * of if it was set or not. To maintain backwards compatibility with existing persisted query resolutions,
 * we need to check for *both* versions of the query with and without this property. This is because the
 * lookupSortType field is optional and can be undefined or null. This difference normally would provide
 * equivalent results of the query, but will not match here because JSON.stringify() produces different
 * results for null and undefined.
 *
 * See BNCH-111932 for more details.
 */
function findMatchingLookupResolutionWithSortTypeRegression(
  resolutions: QueryResolutionMapping,
  query: LookupColumnQuery,
  tableProvenanceUuid: string
): QueryResolution | null {
  // create a version of the query with {lookupSortType: null} instead of just {} or vice versa to check
  // for queries that should be considered equivalent
  const queryWithOtherEmptyLookupSortType: LookupColumnQuery = produce(query, (draft) => {
    if (query.lookupSortType === undefined) {
      draft.lookupSortType = null;
      // eslint-disable-next-line benchlint/no-eqeqeq-null -- we specifically need to check for null instead of undefined
    } else if (query.lookupSortType === null) {
      delete draft.lookupSortType;
    }
  });
  return (
    resolutions[_generateQueryResolutionKey(queryWithOtherEmptyLookupSortType, tableProvenanceUuid)] ?? null
  );
}

export function _findMatchingResolution(
  resolutions: QueryResolutionMapping,
  query: ResolutionQuery,
  tableProvenanceUuid: string
): QueryResolution | null {
  if (isLookupColumnQuery(query)) {
    const matchingLookupResolutionWithSortTypeRegression = findMatchingLookupResolutionWithSortTypeRegression(
      resolutions,
      query,
      tableProvenanceUuid
    );
    if (matchingLookupResolutionWithSortTypeRegression) {
      return matchingLookupResolutionWithSortTypeRegression;
    }
  }

  return resolutions[_generateQueryResolutionKey(query, tableProvenanceUuid)] ?? null;
}

export function mergeResolutions(
  existingResolutions: Array<QueryResolution>,
  newResolutions: Array<QueryResolution>
): Array<QueryResolution> {
  // Drop new error resolutions whose existing counterpart already has the same error,
  // to prevent timestamp churn that causes infinite version saves (CUST-11651).
  const keptNewResolutions = newResolutions.filter((newRes) => {
    if (newRes.error == null) {
      return true;
    }
    const existingMatch = existingResolutions.find(
      (res) => res.provenanceUuid === newRes.provenanceUuid && isEqual(res.query, newRes.query)
    );
    if (existingMatch?.error == null) {
      return true;
    }
    return !ExcessUtil.containsError(existingMatch.error, newRes.error);
  });

  return existingResolutions
    .filter(
      (res) =>
        !keptNewResolutions.some(
          (newRes) => newRes.provenanceUuid === res.provenanceUuid && isEqual(newRes.query, res.query)
        )
    )
    .concat(keptNewResolutions);
}

async function toQueryResolutionPromise(
  resolution: QueryResolution,
  resolutionPromise: Promise<{resolvedValue: TypedValue | null; error: ExcessUtil.CellError | null}>
): Promise<QueryResolution> {
  const {resolvedValue, error} = await resolutionPromise;
  return {
    ...resolution,
    resolvedValue,
    error: error ? error.toJSON() : null,
    timestamp: new Date().toISOString(),
  };
}

export function _getUnresolvedResolution(query: ResolutionQuery, tableId: string): QueryResolution {
  return {
    userId: EntityStore.currentUserId,
    timestamp: new Date().toISOString(),
    provenanceUuid: tableId,
    resolvedValue: null,
    error: null,
    query,
  };
}

// Helper functions to handle removing stale query resolutions
export function removeQueryResolutionsFromSnapshot(
  itemId: string,
  provenanceUuid: string,
  tableOp: TableOpDiffMap,
  lookupConfigIds?: Array<string>
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE,
    itemId,
    provenanceUuid,
    tableOp,
    lookupConfigIds,
  });
}

export function removeQueryResolutionsForTableFromSnapshotInNote<T extends Note>(
  note: T,
  provenanceUuid: string,
  lookupConfigIds?: Array<string>
): T {
  if (note == null || note.noteRuntimeSnapshot == null) {
    return note;
  }

  const updatedSnapshot = removeQueryResolutionsForTableFromSnapshot(
    note.noteRuntimeSnapshot,
    provenanceUuid,
    lookupConfigIds
  );

  return produce(note, (draftNote) => {
    draftNote.noteRuntimeSnapshot = updatedSnapshot;
  });
}

export function removeQueryResolutionsForTableFromSnapshot(
  noteRuntimeSnapshot: NoteRuntimeSnapshot,
  provenanceUuid: string,
  lookupConfigIds?: Array<string>
): NoteRuntimeSnapshot {
  if (noteRuntimeSnapshot == null) {
    return noteRuntimeSnapshot;
  }
  let remainingResolutions = noteRuntimeSnapshot.queryResolutions;
  if (lookupConfigIds) {
    // Filter out all lookup queries that match the provenanceUuid
    remainingResolutions = filterLookupColumnQueries(remainingResolutions, provenanceUuid, lookupConfigIds);
  } else {
    remainingResolutions = noteRuntimeSnapshot.queryResolutions.filter(
      (query) => query.provenanceUuid !== provenanceUuid
    );
  }

  return produce(noteRuntimeSnapshot, (draftSnapshot) => {
    draftSnapshot.queryResolutions = remainingResolutions;
  });
}

/**
 * Resolvers should be instantiated once per full evaluation to resolve queries into typedValues,
 * exposing `resolveTypedValueWithSnapshot` for resolving and `getResolutions` for tracking the resolutions.
 */
export default class Resolver {
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  // The following QueryResolutionMappings provide O(1) lookup while performing resolutions:
  //  - prevResolutionsMap are the persisted query resolutions from the existing noteRuntimeSnapshot
  //  - currentResolutionsMap are the to-be persisted query resolutions to replace those in the noteRuntimeSnapshot
  prevResolutionsMap: QueryResolutionMapping;
  currentResolutionsMap: QueryResolutionMapping;
  asyncResolutions: Array<Promise<QueryResolution>>;
  cellResolutionsMap: CellToQueryResolutionsMapping;
  cellsWithUnresolvedResolutionQueries: Set<string>;
  _getCurrentTableProvenanceUuid: () => string;
  _resolveFromResolutionQueryPromise: (
    typedValue: TypedValue,
    resolutionQuery: ResolutionQuery,
    makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue | null,
    waitSeconds: number
  ) => Promise<ResolvedValueInfo>;

  constructor(noteRuntimeSnapshot: NoteRuntimeSnapshot, getCurrentTableProvenanceUuid: () => string) {
    this.noteRuntimeSnapshot = noteRuntimeSnapshot;
    // Build a map of resolutions from snapshot at Resolver instantiation for O(1) lookup.
    this.prevResolutionsMap = _constructQueryResolutionMap(this.noteRuntimeSnapshot.queryResolutions);
    this.currentResolutionsMap = {};
    this.asyncResolutions = [];
    this.cellResolutionsMap = {};
    this.cellsWithUnresolvedResolutionQueries = new Set();
    this._getCurrentTableProvenanceUuid = getCurrentTableProvenanceUuid;
    this._resolveFromResolutionQueryPromise = resolveFromResolutionQueryPromise; // to mock
  }

  getQueryResolutionsForCell(cellId: string): Array<QueryResolution> | null {
    return this.cellResolutionsMap[cellId];
  }

  /**
   * Enumerate the new set of resolutions.
   *
   * syncResolutions: covers all queries that are still present
   *    - A resolution that is still present will be carried over from noteRuntimeSnapshot.queryResolutions.
   *    - A resolution that is no longer present will be missing from currentResolutionsMap.
   *    - A resolution that is new will be presented as a resolution with no resolved value.
   *
   * asyncResolutions: For any new resolution, expose promises that a caller can wait on for any resolution
   * that wasn't already present in noteRuntimeSnapshot.
   */
  getResolutions(): {
    syncResolutions: Array<QueryResolution>;
    asyncResolutions: Array<Promise<QueryResolution>>;
    cellsWithUnresolvedResolutionQueries: Set<string>;
  } {
    return {
      syncResolutions: Object.values(this.currentResolutionsMap),
      asyncResolutions: this.asyncResolutions,
      cellsWithUnresolvedResolutionQueries: this.cellsWithUnresolvedResolutionQueries,
    };
  }

  /**
   * Function that returns a counterpart to resolveTypedValueWithRedux that queries againt noteRuntimeSnapshot instead of
   * redux.
   *
   * Also tracks new / existing queries, exposed via this.getResolutions() and getQueryResolutionsForCell().
   */
  // oxlint-disable-next-line arrow-body-style
  getResolveTypedValueFnForCell = (cellId: string): ResolveTypedValueToTypeFn => {
    return (
      columnDefinition: ColumnDefinition,
      typedValue: TypedValue,
      options: Partial<ResolutionContext> = {}
    ): ResolvedValueInfo =>
      resolveTypedValue(
        this.getResolveQueryFnForCell(cellId),
        columnDefinition,
        typedValue,
        this.noteRuntimeSnapshot.languageTag,
        options
      );
  };

  _addToCurrentQueryResolutions(resolution: QueryResolution): void {
    this.currentResolutionsMap[_generateQueryResolutionKey(resolution.query, resolution.provenanceUuid)] =
      resolution;
  }

  _mapQueryResolutionToCell(cellId: string, queryResolution: QueryResolution): void {
    const resolutions = this.cellResolutionsMap[cellId] ?? [];
    resolutions.push(queryResolution);
    this.cellResolutionsMap[cellId] = resolutions;
  }

  /**
   * Given an existing query, track it if it hasn't been tracked already. This includes untracked queries that
   * exist in the note runtime snapshot.
   *
   * If it's a brand new query, queue it for execution, and track it.
   *
   * @returns (newly) tracked query resolution
   */
  _trackQuery(
    query: ResolutionQuery,
    typedValue: TypedValue,
    cellId: string,
    makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue | null
  ): QueryResolution | null {
    const tableProvenanceUuid = this._getCurrentTableProvenanceUuid();
    const trackedResolution = _findMatchingResolution(this.currentResolutionsMap, query, tableProvenanceUuid);

    // If the query has already been tracked, add the cellId to the set of cells with unresolved queries if not resolved,
    // otherwise, NOOP.
    if (trackedResolution != null) {
      this._mapQueryResolutionToCell(cellId, trackedResolution);
      if (!this._isQueryResolutionResolved(trackedResolution)) {
        this.cellsWithUnresolvedResolutionQueries.add(cellId);
      }
      return trackedResolution;
    }
    // Otherwise, check whether it exists in the snapshot.
    const existingResolution = _findMatchingResolution(this.prevResolutionsMap, query, tableProvenanceUuid);
    if (existingResolution) {
      // If it does, track it.
      this._addToCurrentQueryResolutions(existingResolution);
      // If an incomplete resolution was persisted in the snapshot, requeue the query.
      if (!this._isQueryResolutionResolved(existingResolution)) {
        const asyncResolution = toQueryResolutionPromise(
          existingResolution,
          this._resolveFromResolutionQueryPromise(
            typedValue,
            query,
            makeTypedValueFromResolvedValue,
            RESOLUTION_TIMEOUT_SECONDS
          )
        );
        this._trackAsyncResolutionPromise(asyncResolution, cellId);
      }
      this._mapQueryResolutionToCell(cellId, existingResolution);
      return existingResolution;
    } else {
      // If it doesn't, the query must be new, so queue it in asyncResolutions and track it.
      const baseResolution = _getUnresolvedResolution(query, tableProvenanceUuid);
      const asyncResolution = toQueryResolutionPromise(
        baseResolution,
        this._resolveFromResolutionQueryPromise(
          typedValue,
          query,
          makeTypedValueFromResolvedValue,
          RESOLUTION_TIMEOUT_SECONDS
        )
      );
      this._trackAsyncResolutionPromise(asyncResolution, cellId);
      this._addToCurrentQueryResolutions(baseResolution);

      this._mapQueryResolutionToCell(cellId, baseResolution);
      return baseResolution;
    }
  }

  _trackAsyncResolutionPromise(resolution: Promise<QueryResolution>, cellId: string): void {
    this.asyncResolutions.push(resolution);
    this.cellsWithUnresolvedResolutionQueries.add(cellId);
  }

  _isQueryResolutionResolved(resolution: QueryResolution): boolean {
    return resolution.resolvedValue != null && resolution.error?.type !== ExcessUtil.LoadingError.type;
  }

  /**
   * Returns a function that is a counterpart to _resolveFromResolutionQueryNoWait that queries against
   * this.noteRuntimeSnapshot.
   *
   * Also tracks new / existing queries, exposed via this.getResolutions() and getQueryResolutionsForCell().
   *
   * @param cellId the map key to associate any query resolutions against
   */
  // oxlint-disable-next-line arrow-body-style
  getResolveQueryFnForCell = (cellId: string): ResolveQueryFn => {
    return (
      typedValue: TypedValue,
      query: ResolutionQuery,
      makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue
    ): ResolvedValueInfo => {
      const resolution = this._trackQuery(query, typedValue, cellId, makeTypedValueFromResolvedValue);

      if (resolution?.resolvedValue) {
        const {resolvedValue, error} = resolution;
        return {
          resolvedValue,
          error: error ? ExcessUtil.CellError.fromJSON(error) : null,
        };
      } else {
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.LoadingError(''),
        };
      }
    };
  };
}
